import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';

import TextField from '../components/TextField';
import FullScreenDialog from '../components/FullScreenDialog';
import DateTimePicker from '../components/DateTimePicker';

import useDiaryData from '../hooks/useDiaryData';
import useQueryParams from '../hooks/useQueryParams';
import useSnackbarText from '../hooks/useSnackbarText';

import birthdates from '../api/birthdates';
import { maxWidth } from '../components/configs';

const queryParamKey = 'create-birthdate';

const generateDefaultState = (selectedDate) => {
	return {
		date: selectedDate,
		name: '',
		contact: '',
	}
}

const useStyles = makeStyles(theme => ({
	container: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center'
	},
	submitButton: {
		maxWidth,
		width: '100%',
		marginTop: 20,
		marginBottom: 20,
		color: '#fff',
		borderRadius: 6,
		fontWeight: 600,
		backgroundColor: theme.palette.success.main,
		'&:hover': {
			backgroundColor: theme.palette.success.dark,
		}
	}
}));

function CreateBirthdateDialog() {
	const classes = useStyles();
	// eslint-disable-next-line
	const [diary, _, handleSetDate, handleManualFetch] = useDiaryData();
	const [queryParams, setQueryParams] = useQueryParams();
	const [disabled, setDisabled] = useState(false);
	const [state, _setState] = useState(generateDefaultState(diary.selectedDate));
	const [error, setError] = useState({});
	const handleSnackbarChange = useSnackbarText();

	const queryValue = queryParams[queryParamKey];

	const setState = useCallback((newState) => {
		_setState(prevState => ({
			...prevState,
			...newState,
		}));
	}, []);

	const handleChange = (type) => (event, value) => {
		if(value !== undefined) setState({ [type]: value });
		else setState({ [type]: event.target.value });
	};

	const handleDateChange = (type) => (event, value) => {
		setState({ [type]: value });
	};

	const handleValueCheck = () => {
		let noError = true;
		let error = {};
		if(!state.name) {
			noError = false;
			error.name = 'Invalid value.';
		}

		if(!state.date) {
			noError = false;
			error.date = 'Invalid value.';
		}
		setError(error);
		return noError;
	};

	const handleSubmit = async () => {
		if(!handleValueCheck()) {
			return handleSnackbarChange({ text: 'Invalid inputs', variant: 'error' });
		} 

		setDisabled(true);
		const { data, success } = await birthdates.createBirthdate({
			name: state.name,
			date: new Date(state.date),
			contact: state.contact,
		});
		setDisabled(false);

		if(!success) {
			return handleSnackbarChange({ text: data.message, variant: 'error' });
		} else {
			handleSnackbarChange({ text: 'Birthdate created', variant: 'success' });
			handleManualFetch();
			setQueryParams({ [queryParamKey]: null });
		}
	};

	useEffect(() => {
		_setState(generateDefaultState(diary.selectedDate));
	}, [queryValue, diary.selectedDate]);

	return (
		<FullScreenDialog 
			title="Create New Birthdate"
			queryParamKey={queryParamKey}
		>
			<div className={classes.container}>
				<TextField 
					label="Name*"
					value={state.name}
					onChange={handleChange('name')}
					error={Boolean(error.name)}
					helperText={error.name}
				/>

				<TextField 
					label="Contact"
					value={state.contact}
					onChange={handleChange('contact')}
				/>

				<DateTimePicker 
					label="Date"
					value={state.date}
					onChange={handleDateChange('date')}
					error={Boolean(error.date)}
					helperText={error.date}
				/>

				<Button 
					className={classes.submitButton}
					onClick={handleSubmit}
					disabled={disabled}
				>
					Create
				</Button>
			</div>
		</FullScreenDialog>
	);
}

export default CreateBirthdateDialog;
